import { useState, type FormEvent } from 'react';
import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import type { Contact, ContactStatus } from '@/types/contacts';
import {
  STATUS_DOT_COLORS,
  STATUS_LABELS,
  STATUS_ORDER,
  markAsLabel,
  statusBadge,
} from '@/lib/status';
import { formatCurrency } from '@/lib/format';
import { useCurrency } from '@/lib/useAuth';
import type { ContactsListResponse } from '@/lib/contacts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Walk the useInfiniteQuery cache structure and patch a contact in place.
// Returns a new InfiniteData object — keeps React equality-based reconciliation
// honest (no mutation of the original cache). Used for the optimistic update on
// the status mutation.
function patchContactInPages(
  data: InfiniteData<ContactsListResponse> | undefined,
  contactId: string,
  patch: Partial<Contact>,
): InfiniteData<ContactsListResponse> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((c) =>
        c.id === contactId ? { ...c, ...patch } : c,
      ),
    })),
  };
}

interface TransitionVars {
  to_status: ContactStatus;
  value?: number;
  notes?: string;
}

export function StatusDropdown({
  contact,
  compact = false,
}: {
  contact: Contact;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [wonSaleOpen, setWonSaleOpen] = useState(false);
  const [wonSaleValueInput, setWonSaleValueInput] = useState('');
  const [wonSaleNotesInput, setWonSaleNotesInput] = useState('');
  const [wonSaleError, setWonSaleError] = useState<string | null>(null);
  const currency = useCurrency();
  const queryClient = useQueryClient();

  const transition = useMutation({
    // Global MutationCache.onError (main.tsx) reads `meta.errorMessage` and
    // fires a Sonner toast on failure.
    meta: { errorMessage: "Couldn't update status — try again" },
    mutationFn: (vars: TransitionVars) =>
      apiRequest(`/contacts/${contact.id}/transition`, {
        method: 'POST',
        body: { ...vars },
      }),
    // Optimistic update. Flip the row instantly so the dropdown click feels
    // zero-latency; reconcile via onSettled.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['contacts'] });
      const snapshots = queryClient.getQueriesData<
        InfiniteData<ContactsListResponse>
      >({ queryKey: ['contacts'] });
      const patch: Partial<Contact> = { status: vars.to_status };
      if (vars.value !== undefined) patch.value = vars.value;
      if (vars.notes !== undefined) patch.notes = vars.notes;
      queryClient.setQueriesData<InfiniteData<ContactsListResponse>>(
        { queryKey: ['contacts'] },
        (data) => patchContactInPages(data, contact.id, patch),
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback every snapshotted query. Global MutationCache.onError still
      // fires the toast — it runs in addition to the per-mutation onError.
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      // Reconcile after the server has the last word — picks up side effects
      // (firing conversions, updated_at bump, etc.) the optimistic update
      // couldn't know about.
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
  });

  function pickStatus(to: ContactStatus) {
    setOpen(false);
    if (to === contact.status) return;
    if (to === 'purchase') {
      // shadcn Dialog replaces window.prompt. Prefill with existing value (if
      // any) so revising a won sale is one click + Enter.
      setWonSaleValueInput(contact.value != null ? String(contact.value) : '');
      setWonSaleNotesInput(contact.notes ?? '');
      setWonSaleError(null);
      setWonSaleOpen(true);
      return;
    }
    transition.mutate({ to_status: to });
  }

  function submitWonSale(e: FormEvent) {
    e.preventDefault();
    const v = Number(wonSaleValueInput);
    if (!Number.isFinite(v) || v < 0) {
      setWonSaleError('Enter a non-negative number.');
      return;
    }
    const vars: TransitionVars = { to_status: 'purchase', value: v };
    const trimmedNotes = wonSaleNotesInput.trim();
    if (trimmedNotes) vars.notes = trimmedNotes;
    transition.mutate(vars);
    setWonSaleOpen(false);
  }

  const badge = statusBadge(contact.status);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={transition.isPending}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded px-2 py-1 text-base transition-colors hover:bg-dark/5 disabled:opacity-50"
        aria-label="Change status"
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${badge.dot}`} />
        <span className={`font-semibold ${badge.text}`}>
          {STATUS_LABELS[contact.status]}
        </span>
        {contact.status === 'purchase' && contact.value != null && (
          <span className="font-bold text-dark">
            · {formatCurrency(contact.value, currency)}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open && (
        <>
          {/* Click-outside scrim */}
          <div
            className="fixed inset-0 z-30"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            className={`absolute top-full z-40 mt-1 min-w-[160px] rounded-lg border border-dark/10 bg-white py-1 shadow-lg ${compact ? 'right-0' : 'right-0'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {STATUS_ORDER.map((s) => (
              <StatusMenuItem
                key={s}
                dot={STATUS_DOT_COLORS[s]}
                onClick={() => pickStatus(s)}
              >
                {STATUS_LABELS[s]}
              </StatusMenuItem>
            ))}
          </div>
        </>
      )}

      {/* Purchase value entry */}
      <Dialog open={wonSaleOpen} onOpenChange={setWonSaleOpen}>
        <DialogContent
          className="sm:max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>{markAsLabel('purchase')}</DialogTitle>
            <DialogDescription>
              Record the sale value. Notes are optional — handy for capturing
              what closed the deal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitWonSale} className="space-y-4">
            <div>
              <label
                htmlFor={`won-value-${contact.id}`}
                className="mb-1 block text-sm font-semibold text-dark"
              >
                Sale value
              </label>
              <input
                id={`won-value-${contact.id}`}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                autoFocus
                required
                value={wonSaleValueInput}
                onChange={(e) => {
                  setWonSaleValueInput(e.target.value);
                  setWonSaleError(null);
                }}
                placeholder="0.00"
                className="w-full rounded-lg border border-dark/15 bg-white px-3 py-2 text-base text-dark placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              {wonSaleError && (
                <p className="mt-1 text-xs text-red-700">{wonSaleError}</p>
              )}
            </div>
            <div>
              <label
                htmlFor={`won-notes-${contact.id}`}
                className="mb-1 block text-sm font-semibold text-dark"
              >
                Notes{' '}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </label>
              <textarea
                id={`won-notes-${contact.id}`}
                rows={3}
                value={wonSaleNotesInput}
                onChange={(e) => setWonSaleNotesInput(e.target.value)}
                placeholder="What closed it?"
                className="w-full resize-none rounded-lg border border-dark/15 bg-white px-3 py-2 text-sm text-dark placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setWonSaleOpen(false)}
                className="inline-flex items-center justify-center rounded-lg border border-dark/15 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:border-dark/30"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-brand-light"
              >
                Save sale
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusMenuItem({
  dot,
  onClick,
  children,
}: {
  dot: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm font-semibold text-dark hover:bg-bg-tint"
    >
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {children}
    </button>
  );
}
